import React, { useState } from 'react';
import { Upload, X, FileJson, AlertCircle } from 'lucide-react';
import { ResponsiveOverlay } from '@/components/ui/ResponsiveOverlay';
import { getStorageData, setStorageData } from '@/lib/storage';
import { useAuth } from '@/contexts/AuthContext';
import toast from 'react-hot-toast';
import { Button } from '@/components/ui/Button';

export function WorkspaceImportDialog({ className, children, externalOpen, setExternalOpen }: { className?: string, children?: React.ReactNode, externalOpen?: boolean, setExternalOpen?: (v: boolean) => void }) {
  const [internalOpen, setInternalOpen] = useState(false);
  const isOpen = externalOpen !== undefined ? externalOpen : internalOpen;
  
  const setIsOpen = (v: boolean) => {
    if (setExternalOpen) setExternalOpen(v);
    else setInternalOpen(v);
  };
  
  const [importedData, setImportedData] = useState<any | null>(null);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [isImporting, setIsImporting] = useState(false);
  
  // Resolution State
  const [resolutionMode, setResolutionMode] = useState(false);
  const [pendingWorkspaces, setPendingWorkspaces] = useState<any[]>([]);
  const [resolutionSelectedIds, setResolutionSelectedIds] = useState<Set<string>>(new Set());
  
  const { user } = useAuth();

  const handleOpen = () => {
    setIsOpen(true);
    setImportedData(null);
    setSelectedIds(new Set());
    setResolutionMode(false);
    setPendingWorkspaces([]);
    setResolutionSelectedIds(new Set());
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (event) => {
      try {
        const json = JSON.parse(event.target?.result as string);
        if (json.version && json.workspaces && Array.isArray(json.workspaces)) {
          setImportedData(json);
          setSelectedIds(new Set(json.workspaces.map((w: any) => w.metadata.id)));
        } else {
          toast.error('Invalid backup file format.');
        }
      } catch (err) {
        toast.error('Failed to parse the file.');
      }
    };
    reader.readAsText(file);
    // clear value to allow re-selection of the same file
    e.target.value = '';
  };

  const handleToggleSelectAll = () => {
    if (!importedData) return;
    if (selectedIds.size === importedData.workspaces.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(importedData.workspaces.map((w: any) => w.metadata.id)));
    }
  };

  const handleToggleWorkspace = (id: string) => {
    const newSet = new Set(selectedIds);
    if (newSet.has(id)) {
      newSet.delete(id);
    } else {
      newSet.add(id);
    }
    setSelectedIds(newSet);
  };

  const saveFinalWorkspaces = async (finalList: any[]) => {
      const allowedIds = new Set(finalList.map(w => w.id));
      let resumesTruncated = false;
      let itemsTruncated = false;

      for (const w of importedData.workspaces) {
        if (selectedIds.has(w.metadata.id) && allowedIds.has(w.metadata.id)) {
          // Inner limits check
          if (w.data) {
             if (w.data.resumes && w.data.resumes.length > 10) { w.data.resumes = w.data.resumes.slice(0, 10); resumesTruncated = true; }
             if (w.data.savedItems && w.data.savedItems.length > 20) { w.data.savedItems = w.data.savedItems.slice(0, 20); itemsTruncated = true; }
             if (w.data.historyPast && w.data.historyPast.length > 10) { w.data.historyPast = w.data.historyPast.slice(w.data.historyPast.length - 10, w.data.historyPast.length); }
             if (w.data.historyFuture && w.data.historyFuture.length > 10) { w.data.historyFuture = w.data.historyFuture.slice(0, 10); }
          }
          await setStorageData(`workspace_${w.metadata.id}`, w.data, user?.uid);
        }
      }

      let limitWarning = '';
      if (resumesTruncated) limitWarning += 'Some resumes truncated (10 max). ';
      if (itemsTruncated) limitWarning += 'Some saved items truncated (20 max). ';

      await setStorageData('resumeChecker_workspaces_v3', finalList, user?.uid);

      if (limitWarning) {
        toast.success(`Imported. ${limitWarning}`);
      } else {
        toast.success('Workspaces imported successfully!');
      }
      
      setIsOpen(false);
      setTimeout(() => { window.location.reload(); }, 1500);
      setIsImporting(false);
  };

  const handleImport = async () => {
    if (!importedData || selectedIds.size === 0) {
      toast.error('Please select at least one workspace to import.');
      return;
    }

    setIsImporting(true);
    try {
      const existingWorkspaces = (await getStorageData<any[]>('resumeChecker_workspaces_v3', user?.uid)) || [];
      const existingIds = new Set(existingWorkspaces.map(w => w.id));

      let newWorkspaces = [...existingWorkspaces];
      
      for (const w of importedData.workspaces) {
        if (selectedIds.has(w.metadata.id)) {
          // If exists, replace it
          if (existingIds.has(w.metadata.id)) {
            const index = newWorkspaces.findIndex(x => x.id === w.metadata.id);
            if (index !== -1) {
              newWorkspaces[index] = w.metadata;
            }
          } else {
            newWorkspaces.push(w.metadata);
          }
        }
      }

      // Filter limits for workspaces BEFORE saving data
      const tailorWorkspaces = newWorkspaces.filter(w => !w.appMode || w.appMode === 'tailor');
      const searchWorkspaces = newWorkspaces.filter(w => w.appMode === 'job_search');

      if (tailorWorkspaces.length > 10 || searchWorkspaces.length > 10) {
         setPendingWorkspaces(newWorkspaces);
         setResolutionSelectedIds(new Set(newWorkspaces.map(w => w.id)));
         setResolutionMode(true);
         setIsImporting(false);
         return;
      }

      await saveFinalWorkspaces(newWorkspaces);
    } catch (err) {
      console.error('Import failed', err);
      toast.error('Failed to import data.');
      setIsImporting(false);
    }
  };

  const handleResolutionConfirm = async () => {
    setIsImporting(true);
    try {
      const finalList = pendingWorkspaces.filter(w => resolutionSelectedIds.has(w.id));
      await saveFinalWorkspaces(finalList);
    } catch(err) {
      console.error(err);
      toast.error('Failed to resolve and import data.');
      setIsImporting(false);
    }
  };

  const handleToggleResolution = (id: string) => {
    const newSet = new Set(resolutionSelectedIds);
    if (newSet.has(id)) newSet.delete(id);
    else newSet.add(id);
    setResolutionSelectedIds(newSet);
  };

  const isResolutionValid = ['tailor', 'job_search'].every(mode => {
     const count = pendingWorkspaces.filter(w => resolutionSelectedIds.has(w.id) && (w.appMode === mode || (!w.appMode && mode === 'tailor'))).length;
     return count <= 10;
  });

  return (
    <>
      <button
        onClick={handleOpen}
        aria-label="Import Data"
        title="Import Workspaces Data"
        className={className || "flex items-center gap-1.5 text-xs font-medium text-slate-700 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors cursor-pointer h-8 px-2 sm:px-3 rounded-md"}
      >
        {children || (
          <>
            <Upload className="w-4 h-4 sm:w-3.5 sm:h-3.5" />
            <span className="hidden sm:inline">Import</span>
          </>
        )}
      </button>

      <ResponsiveOverlay
        isOpen={isOpen}
        onClose={() => setIsOpen(false)}
        variant="modal"
        className="w-full sm:max-w-md max-h-[90vh] flex flex-col"
      >
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200 dark:border-slate-800 shrink-0 bg-slate-50/50 dark:bg-slate-900/50">
          <div className="flex items-center gap-2">
            <div className="bg-emerald-100 dark:bg-emerald-500/20 p-1.5 rounded-md">
              <FileJson className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
            </div>
            <h2 className="text-base font-semibold text-slate-900 dark:text-white">Import Data</h2>
          </div>
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setIsOpen(false)}
            aria-label="Close Import Dialog"
            className="rounded-lg text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-300"
          >
            <X className="w-5 h-5" />
          </Button>
        </div>

        <div className="overflow-y-auto p-6 flex flex-col gap-4">
          <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800/50 p-3 rounded-lg flex items-start gap-2">
            <AlertCircle className="w-5 h-5 text-amber-600 dark:text-amber-500 shrink-0 mt-0.5" />
            <div className="text-xs text-amber-800 dark:text-amber-400">
              <strong>Warning:</strong> Importing workspaces with the same ID will overwrite your current local data for those workspaces.
            </div>
          </div>

          {resolutionMode ? (
            <>
              <div className="bg-rose-50 dark:bg-rose-900/20 border border-rose-200 dark:border-rose-800/50 p-4 rounded-lg flex flex-col gap-2">
                <div className="flex items-start gap-2">
                   <AlertCircle className="w-5 h-5 text-rose-600 dark:text-rose-500 shrink-0 mt-0.5" />
                   <div className="text-sm font-semibold text-rose-900 dark:text-rose-400">Workspace Limits Exceeded</div>
                </div>
                <div className="text-xs text-rose-800 dark:text-rose-300 ml-7 space-y-1">
                  <p>You can have a maximum of <strong>10</strong> Resume Analyze and <strong>10</strong> Job Search workspaces.</p>
                  <p>Please deselect workspaces to proceed. <strong className="font-bold underline">Deselecting an existing workspace will permanently delete it.</strong></p>
                </div>
              </div>
              
              <div className="space-y-4">
                {['tailor', 'job_search'].map(mode => {
                   const wsList = pendingWorkspaces.filter(w => w.appMode === mode || (!w.appMode && mode === 'tailor'));
                   const selectedCount = wsList.filter(w => resolutionSelectedIds.has(w.id)).length;
                   const isOverLimit = selectedCount > 10;
                   if (wsList.length === 0) return null;
                   
                   return (
                     <div key={mode} className="flex flex-col gap-2">
                       <div className="flex items-center justify-between">
                         <h4 className="text-sm font-semibold text-slate-900 dark:text-white">
                           {mode === 'tailor' ? 'Resume Analyze' : 'Job Search'}
                         </h4>
                         <span className={`text-xs font-bold px-2.5 py-0.5 rounded-full outline outline-1 outline-offset-1 ${isOverLimit ? 'bg-rose-100 text-rose-700 outline-rose-300/50 dark:bg-rose-900/50 dark:text-rose-400' : 'bg-emerald-100 text-emerald-700 outline-emerald-300/50 dark:bg-emerald-900/50 dark:text-emerald-400'}`}>
                           {selectedCount} / 10
                         </span>
                       </div>
                       
                       <div className="space-y-2 max-h-[200px] overflow-y-auto scrollbar-thin scrollbar-thumb-slate-200 dark:scrollbar-thumb-slate-700 pr-2">
                          {wsList.map(w => {
                             const isSelected = resolutionSelectedIds.has(w.id);
                             const isNew = importedData.workspaces.some((imported: any) => imported.metadata.id === w.id);
                             
                             return (
                               <div 
                                 key={w.id} 
                                 onClick={() => handleToggleResolution(w.id)}
                                 className={`flex items-start gap-3 p-3 rounded-xl border transition-all cursor-pointer ${
                                   isSelected
                                     ? 'border-indigo-500 bg-indigo-50 dark:bg-indigo-500/10 dark:border-indigo-500/50' 
                                     : 'border-slate-200 dark:border-slate-800 opacity-60 bg-slate-50 dark:bg-slate-900 shadow-inner hover:opacity-100 hover:border-slate-400 dark:hover:border-slate-600'
                                 }`}
                               >
                                 <div className="flex h-5 items-center mt-0.5">
                                   <input
                                     type="checkbox"
                                     checked={isSelected}
                                     onChange={() => {}}
                                     className="w-4 h-4 rounded border-slate-300 text-indigo-600 focus:ring-indigo-600 cursor-pointer"
                                   />
                                 </div>
                                 <div className="flex-1 min-w-0 flex flex-col justify-center">
                                   <p className={`text-sm font-medium ${isSelected ? 'text-indigo-900 dark:text-indigo-100' : 'text-slate-500 line-through'} truncate`}>
                                     {w.role || 'Untitled Workspace'}
                                   </p>
                                   <p className={`text-xs ${isSelected ? 'text-indigo-700 dark:text-indigo-300' : 'text-slate-400'} truncate`}>
                                      {w.company} {w.updatedAt ? `• ${new Date(w.updatedAt).toLocaleDateString()}` : ''}
                                   </p>
                                 </div>
                                 <div className="flex items-center shrink-0">
                                   {isNew ? (
                                     <span className={`text-[10px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded ${isSelected ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-500/20 dark:text-emerald-400' : 'bg-slate-200 text-slate-500 dark:bg-slate-800'}`}>Import</span>
                                   ) : (
                                     <span className={`text-[10px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded ${isSelected ? 'bg-indigo-100 text-indigo-700 dark:bg-indigo-500/20 dark:text-indigo-400' : 'bg-slate-200 text-slate-500 dark:bg-slate-800'}`}>Existing</span>
                                   )}
                                 </div>
                               </div>
                             );
                          })}
                       </div>
                     </div>
                   );
                })}
              </div>
            </>
          ) : !importedData ? (
            <div className="flex flex-col items-center justify-center p-8 border-2 border-dashed border-slate-300 dark:border-slate-700 rounded-xl cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors" onClick={() => document.getElementById('import-file-upload')?.click()}>
               <Upload className="w-8 h-8 text-slate-400 mb-3" />
               <p className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Click to upload JSON file</p>
               <p className="text-xs text-slate-500">Only previously exported JSON files are supported</p>
               <input id="import-file-upload" type="file" accept=".json,application/json" className="hidden" onChange={handleFileChange} />
            </div>
          ) : (
            <>
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-sm font-medium text-slate-900 dark:text-slate-100">Found {importedData.workspaces.length} Workspaces</h3>
                <button
                  onClick={handleToggleSelectAll}
                  className="text-xs font-medium text-emerald-600 dark:text-emerald-400 hover:underline cursor-pointer"
                >
                  {selectedIds.size === importedData.workspaces.length ? 'Deselect All' : 'Select All'}
                </button>
              </div>
              
              <div className="space-y-2 max-h-[250px] overflow-y-auto scrollbar-thin scrollbar-thumb-slate-200 dark:scrollbar-thumb-slate-700 pr-2">
                {importedData.workspaces.map((w: any) => (
                  <div 
                    key={w.metadata.id} 
                    onClick={() => handleToggleWorkspace(w.metadata.id)}
                    className={`flex items-start gap-3 p-3 rounded-xl border transition-all cursor-pointer ${
                      selectedIds.has(w.metadata.id) 
                        ? 'border-emerald-500 bg-emerald-50 dark:bg-emerald-500/10 dark:border-emerald-500/50' 
                        : 'border-slate-200 dark:border-slate-800 hover:border-emerald-200 dark:hover:border-emerald-800'
                    }`}
                  >
                    <div className="flex h-5 items-center mt-0.5">
                      <input
                        type="checkbox"
                        checked={selectedIds.has(w.metadata.id)}
                        onChange={() => {}}
                        className="w-4 h-4 rounded border-slate-300 text-emerald-600 focus:ring-emerald-600 cursor-pointer"
                      />
                    </div>
                    <div>
                      <p className={`text-sm font-medium ${selectedIds.has(w.metadata.id) ? 'text-emerald-900 dark:text-emerald-100' : 'text-slate-900 dark:text-white'}`}>
                        {w.metadata.role || 'Untitled Workspace'}
                      </p>
                      <p className={`text-xs ${selectedIds.has(w.metadata.id) ? 'text-emerald-700 dark:text-emerald-300' : 'text-slate-500'}`}>
                         {w.metadata.company} {w.metadata.updatedAt ? `• ${new Date(w.metadata.updatedAt).toLocaleDateString()}` : ''}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
              
              <button
                 onClick={() => setImportedData(null)}
                 className="text-xs text-slate-500 hover:text-slate-700 dark:hover:text-slate-300 underline mt-2 text-center"
              >
                 Choose a different file
              </button>
            </>
          )}
        </div>

        <div className="flex justify-end gap-3 p-4 border-t border-slate-200 dark:border-slate-800 bg-slate-50/80 dark:bg-slate-900/80 shrink-0 sm:rounded-b-2xl">
          <button
            onClick={() => {
               if (resolutionMode) {
                  setResolutionMode(false);
                  setPendingWorkspaces([]);
                  setResolutionSelectedIds(new Set());
               } else {
                  setIsOpen(false);
               }
            }}
            className="px-4 py-2 text-sm font-medium text-slate-700 dark:text-slate-300 hover:text-slate-900 dark:hover:text-white transition-colors cursor-pointer"
          >
            {resolutionMode ? 'Back' : 'Cancel'}
          </button>
          {resolutionMode ? (
            <button
              onClick={handleResolutionConfirm}
              disabled={isImporting || !isResolutionValid}
              className="flex items-center gap-2 bg-indigo-600 hover:bg-indigo-700 text-white font-medium py-2 px-6 rounded-xl transition-colors text-sm cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isImporting ? 'Saving...' : 'Confirm & Save'}
            </button>
          ) : (
            <button
              onClick={handleImport}
              disabled={isImporting || !importedData || selectedIds.size === 0}
              className="flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white font-medium py-2 px-6 rounded-xl transition-colors text-sm cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isImporting ? 'Importing...' : 'Import Selected'}
            </button>
          )}
        </div>
      </ResponsiveOverlay>
    </>
  );
}
